// «Файл» (бэкстейдж, как в Word): сведения об отчёте, версии, экспорт и готовые файлы, печать, Word, закрытие.
// Элементы, которые другие функции регистрируют на вкладку 'file', показываются в разделе «Сведения».
import { useEffect, useState } from 'react';
import { countWords } from '../core/textStats';
import { registry } from '../core/registry';
import { ctx, runCommand } from '../core/ctx';
import { useApp } from '../core/store';
import { useNumbering } from '../core/editor/numberingPlugin';
import { uiStore, useUi } from './uiState';
import { Btn } from './controls';
import { Icon } from './icons';
import { RibbonEntry } from './Ribbon';
import { shellApi, type ExportFile } from '../features/shell/shellApi';

const NAV: [string, string, string][] = [
  ['info', 'Сведения', 'info'], ['versions', 'Версии', 'history'], ['export', 'Экспорт', 'export'], ['print', 'Печать', 'print'],
];
const TYPE_NAMES: Record<string, string> = {
  lab: 'Лабораторная работа', pr: 'Практическая работа', kr: 'Контрольная работа', rgr: 'Расчётно-графическая работа',
  referat: 'Реферат', essay: 'Эссе', kursovaya: 'Курсовая работа', kp: 'Курсовой проект', practice: 'Отчёт о практике',
  sheet: 'Чистый лист',
};

function size(n: number) { return n > 1048576 ? (n / 1048576).toFixed(1) + ' МБ' : Math.max(1, Math.round(n / 1024)) + ' КБ'; }
function when(t: number) { return new Date(t * 1000).toLocaleString('ru-RU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }

export function Backstage() {
  const page = useUi((s) => s.backstage);
  useEffect(() => {
    if (!page) return;
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape' && !document.querySelector('.modal')) { e.preventDefault(); close(); } };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [page]);
  if (!page) return null;
  const close = () => { uiStore.getState().set({ backstage: null }); setTimeout(() => ctx.editor?.commands.focus(), 0); };
  const go = (p: string) => uiStore.getState().set({ backstage: p });
  const act = (id: string) => () => { close(); runCommand(id); };
  return (
    <div className="backstage" role="dialog" aria-label="Файл" data-testid="backstage">
      <nav className="bs-nav">
        <button type="button" className="bs-back" onClick={close} aria-label="Назад к документу" data-tip="Назад к документу" data-tip-key="Esc">
          <Icon name="back" size={18} />
        </button>
        {NAV.map(([id, title, icon]) => (
          <button key={id} type="button" className={'bs-item' + (page === id ? ' is-active' : '')} onClick={() => go(id)} data-testid={'bs-' + id}>
            <Icon name={icon} size={17} /><span>{title}</span>
          </button>
        ))}
        <div className="bs-sep" />
        <button type="button" className="bs-item" onClick={act('file.save')}><Icon name="save" size={17} /><span>Сохранить</span></button>
        {registry.commands.has('file.openWord') && <button type="button" className="bs-item" onClick={act('file.openWord')} data-testid="bs-word"><Icon name="external" size={17} /><span>Открыть в Word</span></button>}
        <button type="button" className="bs-item" onClick={act('project.rename')}><Icon name="rename" size={17} /><span>Переименовать</span></button>
        <button type="button" className="bs-item" onClick={act('project.duplicate')}><Icon name="copy" size={17} /><span>Копия отчёта</span></button>
        <span className="topbar-spacer" />
        <button type="button" className="bs-item" onClick={act('app.help')}><Icon name="help" size={17} /><span>Справка</span></button>
        <button type="button" className="bs-item" onClick={act('file.close')} data-testid="bs-close"><Icon name="close" size={17} /><span>Закрыть отчёт</span></button>
      </nav>
      <main className="bs-main">
        {page === 'info' && <InfoPage />}
        {page === 'versions' && <VersionsPage />}
        {page === 'export' && <ExportPage />}
        {page === 'print' && <PrintPage />}
      </main>
    </div>
  );
}

function InfoPage() {
  const pid = useApp((s) => s.projectId)!;
  const meta = useApp((s) => s.meta);
  const save = useApp((s) => s.save);
  const check = useApp((s) => s.check);
  const nb = useNumbering();
  const pages = useUi((s) => s.pages);
  const extra = registry.ribbonGroups.list().filter((g) => g.tab === 'file');
  const words = ctx.editor ? countWords(ctx.editor.state.doc) : 0;   // так же, как в строке состояния
  const res = check.result;
  return (
    <div className="bs-page">
      <h1>Сведения</h1>
      <div className="bs-info">
        <div className="bs-card bs-doc">
          <div className="bs-kicker">{TYPE_NAMES[meta?.type || ''] || meta?.type}{meta?.number ? ` № ${meta.number}` : ''}</div>
          <div className="bs-title">{meta?.title || <span className="muted">Тема не указана</span>}</div>
          <div className="muted">{meta?.discipline}</div>
          <div className="bs-row">
            <Btn icon="title" label="Титул и данные работы" primary onClick={() => { uiStore.getState().set({ backstage: null }); runCommand('sto.meta'); }} />
            {registry.commands.has('file.reveal') && <Btn icon="open" label="Папка отчёта" onClick={() => runCommand('file.reveal')} />}
          </div>
        </div>
        <div className="bs-card">
          <div className="bs-grid">
            <span>Имя отчёта</span><b>{pid}</b>
            <span>Сохранено</span><b>{save.at ? new Date(save.at).toLocaleString('ru-RU') : '—'}</b>
            <span>Страниц</span><b>≈ {pages.total || '—'}</b>
            <span>Слов</span><b>{words.toLocaleString('ru-RU')}</b>
            <span>Разделов</span><b>{nb.counts.sections}</b>
            <span>Рисунков / таблиц</span><b>{nb.counts.figures} / {nb.counts.tables}</b>
            <span>Формул / листингов</span><b>{nb.counts.equations} / {nb.counts.listings}</b>
            <span>Проверка по СТО</span>
            <b>{res ? <><span className={'sev-chip ' + (res.errors ? 'sev-e' : 'sev-ok')}>E {res.errors}</span> <span className={'sev-chip ' + (res.warnings ? 'sev-w' : 'sev-ok')}>W {res.warnings}</span>
              {check.at ? <span className="muted"> · {new Date(check.at).toLocaleString('ru-RU')}</span> : null}</> : 'не выполнялась'}</b>
          </div>
          <div className="bs-row"><Btn icon="check" label="Проверить по СТО" onClick={() => { uiStore.getState().set({ backstage: null }); runCommand('sto.check'); }} /></div>
        </div>
      </div>
      {extra.map((g) => (
        <section key={g.id} className="bs-extra">
          <h3>{g.title}</h3>
          <div className="bs-row">{registry.ribbonItems.list().filter((i) => i.group === g.id).map((i) => <RibbonEntry key={i.id} item={i} />)}</div>
        </section>
      ))}
    </div>
  );
}

function VersionsPage() {
  const pid = useApp((s) => s.projectId)!;
  const [list, setList] = useState<{ id: string; kind: string; label: string; time: string }[] | null>(null);
  useEffect(() => { ctx.api.versions(pid).then((r) => setList(r.versions)).catch(() => setList([])); }, [pid]);
  const open = (select?: string) => { uiStore.getState().set({ backstage: null }); void ctx.openDialog('shell.versions', select ? { select } : {}); };
  return (
    <div className="bs-page">
      <h1>Версии</h1>
      <p className="muted">Автоснимок делается при сохранении не чаще раза в 10 минут. Свою версию сохраняйте перед большими правками и при сдаче работы - потом можно сравнить, что изменилось, и вернуться.</p>
      <div className="bs-tiles">
        <button type="button" className="bs-tile" onClick={() => runCommand('file.snapshot')}><Icon name="bookmark" size={26} /><b>Сохранить версию</b><span>С подписью: «Черновик», «После замечаний»…</span></button>
        <button type="button" className="bs-tile" onClick={() => runCommand('file.submitted')}><Icon name="badge" size={26} /><b>Сдано преподавателю</b><span>Версия с датой сдачи</span></button>
        <button type="button" className="bs-tile" onClick={() => open()}><Icon name="compare" size={26} /><b>Сравнить и восстановить</b><span>Что изменилось с любой версии</span></button>
      </div>
      <h3>Последние версии</h3>
      <div className="bs-list">
        {list === null && <div className="muted">Загрузка…</div>}
        {list?.length === 0 && <div className="muted">Пока нет ни одной версии.</div>}
        {list?.slice(0, 8).map((v) => (
          <button key={v.id} type="button" className="bs-list-row" onClick={() => open(v.id)}>
            <Icon name={/сдано/i.test(v.label) ? 'badge' : v.kind === 'manual' ? 'bookmark' : 'clock'} size={15} />
            <span className="bs-list-main">{v.label || (v.kind === 'auto' ? 'Автоснимок' : 'Версия')}</span>
            <span className="muted">{new Date(v.time).toLocaleString('ru-RU')}</span>
            <span className="link-btn">Сравнить</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function ExportPage() {
  const pid = useApp((s) => s.projectId)!;
  const task = useUi((s) => s.task);
  const [files, setFiles] = useState<ExportFile[] | null>(null);
  const load = () => shellApi.exports(pid).then((r) => setFiles(r.files)).catch(() => setFiles([]));
  useEffect(() => { void load(); }, [pid, task]);
  const tile = (id: string, icon: string, title: string, text: string, testid: string) => (
    <button type="button" className="bs-tile" disabled={!!task} onClick={() => runCommand(id)} data-testid={testid}>
      <Icon name={icon} size={28} /><b>{title}</b><span>{text}</span>
    </button>
  );
  return (
    <div className="bs-page">
      <h1>Экспорт</h1>
      <p className="muted">Оформление делает тот же сборщик, что проверен тестами: титул, оглавление, нумерация, подписи - строго по СТО.</p>
      {task && <div className="bs-progress"><Icon name="spinner" className="spin" /> {task.label}: {task.progress}</div>}
      <div className="bs-tiles">
        {tile('file.exportDocx', 'docx', 'Документ Word (.docx)', 'Для сдачи и правок в Word', 'bs-exp-docx')}
        {tile('file.exportPdf', 'pdf', 'PDF с закладками', 'Электронная версия (3.12 СТО)', 'bs-exp-pdf')}
        {registry.commands.has('file.openWord') && tile('file.openWord', 'external', 'Открыть в Word', 'Собрать DOCX и открыть его', 'bs-exp-word')}
        {tile('file.exportMd', 'file', 'Markdown', 'report.md + рисунки для «sto build»', 'bs-exp-md')}
        {tile('file.exportZip', 'zip', 'Архив проекта', 'Резервная копия, перенос на другой ПК', 'bs-exp-zip')}
      </div>
      <h3>Готовые файлы {registry.commands.has('file.reveal') && <button type="button" className="link-btn" onClick={() => runCommand('file.reveal')}>открыть папку</button>}</h3>
      <div className="bs-list" data-testid="bs-files">
        {files === null && <div className="muted">Загрузка…</div>}
        {files?.length === 0 && <div className="muted">Пока ничего не собрано.</div>}
        {files?.map((f) => (
          <a key={f.name} className="bs-list-row" href={ctx.api.fileUrl(pid, f.name)} download>
            <Icon name={f.kind === 'pdf' ? 'pdf' : f.kind === 'zip' ? 'zip' : 'docx'} size={15} />
            <span className="bs-list-main">{f.name}</span>
            <span className="muted">{size(f.size)} · {when(f.mtime)}</span>
            <span className="link-btn">Скачать</span>
          </a>
        ))}
      </div>
    </div>
  );
}

function PrintPage() {
  const task = useUi((s) => s.task);
  return (
    <div className="bs-page">
      <h1>Печать</h1>
      <p className="muted">Отчёт печатается из PDF, который сверстал Word, - так страницы, номера и переносы таблиц будут ровно как в сданном файле.</p>
      <div className="bs-tiles">
        <button type="button" className="bs-tile" disabled={!!task} onClick={() => runCommand('file.print')} data-testid="bs-print">
          <Icon name="print" size={28} /><b>Собрать PDF и печатать</b><span>Откроется в новой вкладке; затем Ctrl+P</span>
        </button>
        <button type="button" className="bs-tile" onClick={() => { uiStore.getState().set({ backstage: null }); runCommand('view.preview'); }}>
          <Icon name="eye" size={28} /><b>Предпросмотр страниц</b><span>Миниатюры страниц в панели справа</span>
        </button>
      </div>
      {task && <div className="bs-progress"><Icon name="spinner" className="spin" /> {task.label}: {task.progress}</div>}
    </div>
  );
}
